import React, { useState, useMemo, useRef, useCallback } from 'react';
import { Eye, EyeOff, ChevronUp, ChevronDown, ChevronLeft, ChevronRight, Search, X } from 'lucide-react';

const TemplateControlsPanel = ({
  selectedTemplate,
  AVERY_TEMPLATES,
  onTemplateChange,
  previewImageUrl,
  config,
  onConfigChange,
  titleFitMode,
  onTitleFitModeChange,
  // New props for artwork navigation
  artworks = [],
  currentArtworkIndex = 0,
  onArtworkChange,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearch, setShowSearch] = useState(false);
  
  // Hold-to-repeat for position buttons (uses larger steps)
  const holdIntervalRef = useRef(null);
  const holdTimeoutRef = useRef(null);
  const HOLD_STEP_MULTIPLIER = 3; // Hold moves 3x faster than click
  
  const startHold = useCallback((action) => {
    // Start repeating after a short delay (no immediate action - that's handled by onClick)
    holdTimeoutRef.current = setTimeout(() => {
      holdIntervalRef.current = setInterval(action, 80); // Repeat every 80ms
    }, 250); // Wait 250ms before starting repeat
  }, []);
  
  const stopHold = useCallback(() => {
    if (holdTimeoutRef.current) {
      clearTimeout(holdTimeoutRef.current);
      holdTimeoutRef.current = null;
    }
    if (holdIntervalRef.current) {
      clearInterval(holdIntervalRef.current);
      holdIntervalRef.current = null;
    }
  }, []);

  if (!config) return null;

  const templateDetails = AVERY_TEMPLATES[selectedTemplate];
  if (!templateDetails) return null;

  const { cardWidth, cardHeight, cardsPerRow, cardsPerCol } = templateDetails;
  const cardsPerPage = cardsPerRow * cardsPerCol;

  // Calculate preview dimensions, maintaining aspect ratio
  // Add some padding to prevent clipping
  const previewWidth = 260;
  const previewHeight = Math.round((previewWidth / cardWidth) * cardHeight) + 4;

  // Current artwork info
  const currentArtwork = artworks[currentArtworkIndex];
  const hasMultipleArtworks = artworks.length > 1;

  // Filter artworks based on search
  const filteredArtworks = useMemo(() => {
    if (!searchQuery.trim()) return [];
    const query = searchQuery.toLowerCase();
    return artworks
      .map((art, index) => ({ ...art, originalIndex: index }))
      .filter(art =>
        (art.title && art.title.toLowerCase().includes(query)) ||
        (art.nickname && art.nickname.toLowerCase().includes(query)) ||
        (art.vc_id && art.vc_id.toString().includes(query))
      )
      .slice(0, 8); // Limit results
  }, [searchQuery, artworks]);

  // Navigation handlers
  const goToPrevArtwork = () => {
    if (onArtworkChange && currentArtworkIndex > 0) {
      onArtworkChange(currentArtworkIndex - 1);
    }
  };

  const goToNextArtwork = () => {
    if (onArtworkChange && currentArtworkIndex < artworks.length - 1) {
      onArtworkChange(currentArtworkIndex + 1);
    }
  };

  const selectArtwork = (index) => {
    if (onArtworkChange) {
      onArtworkChange(index);
      setSearchQuery('');
      setShowSearch(false);
    }
  };

  // Helper to update field visibility
  const updateVisibility = (field, value) => {
    const newConfig = {
      ...config,
      fieldVisibility: {
        ...config.fieldVisibility,
        [field]: value,
      },
    };
    onConfigChange(newConfig);
  };

  // Helper to update layout values (font size, position)
  const updateLayout = (field, value) => {
    const newConfig = {
      ...config,
      layout: {
        ...config.layout,
        [field]: value,
      },
    };
    onConfigChange(newConfig);
  };

  // Field configurations - Title now includes overflow setting
  const fieldConfigs = [
    {
      visibilityKey: 'showTitle',
      label: 'Title',
      fontSizeField: 'titleFontSize',
      xField: 'titleX',
      yField: 'titleY',
      defaultSize: 14,
      posStep: 0.02,
      hasTitleOverflow: true, // Special flag for title overflow control
    },
    {
      visibilityKey: 'showArtist',
      label: 'Artist',
      fontSizeField: 'artistFontSize',
      xField: 'artistX',
      yField: 'artistY',
      defaultSize: 10,
      posStep: 0.02,
    },
    {
      visibilityKey: 'showMedium',
      label: 'Medium',
      fontSizeField: 'infoFontSize',
      xField: 'mediumX',
      yField: 'mediumY',
      defaultSize: 10,
      posStep: 0.02,
    },
    {
      visibilityKey: 'showCompletedDate',
      label: 'Year',
      fontSizeField: 'infoFontSize',
      xField: 'completedDateX',
      yField: 'completedDateY',
      defaultSize: 10,
      posStep: 0.02,
    },
    {
      visibilityKey: 'showDimensions',
      label: 'Dimensions',
      fontSizeField: 'infoFontSize',
      xField: 'dimensionsX',
      yField: 'dimensionsY',
      defaultSize: 10,
      posStep: 0.02,
    },
    {
      visibilityKey: 'showPrice',
      label: 'Price',
      fontSizeField: 'priceFontSize',
      xField: 'priceX',
      yField: 'priceY',
      defaultSize: 12,
      posStep: 0.02,
    },
    {
      visibilityKey: 'showPrintsAvailable',
      label: 'Prints',
      fontSizeField: 'printsAvailableFontSize',
      xField: 'printsAvailableX',
      yField: 'printsAvailableY',
      defaultSize: 9,
      posStep: 0.02,
    },
    {
      visibilityKey: 'showAudioStory',
      label: 'Audio',
      sizeField: 'audioIconSize',
      xField: 'audioIconX',
      yField: 'audioIconY',
      defaultSize: 0.2,
      sizeStep: 0.05,
      posStep: 0.02,
    },
    {
      visibilityKey: 'showArtworkNumber',
      label: 'Art #',
      fontSizeField: 'artworkNumberFontSize',
      xField: 'artworkNumberX',
      yField: 'artworkNumberY',
      defaultSize: 10,
      posStep: 0.02,
    },
    {
      visibilityKey: 'showQRCode',
      label: 'QR Code',
      sizeField: 'qrCodeSize',
      xField: 'qrCodeX',
      yField: 'qrCodeY',
      defaultSize: 0.5,
      sizeStep: 0.1,
      posStep: 0.05,
      isQR: true,
    },
    {
      visibilityKey: 'showCutLines',
      label: 'Cut Lines',
      noControls: true,
    },
  ];

  // Render a single field chip
  const renderFieldChip = (fieldConfig) => {
    const {
      visibilityKey,
      label,
      fontSizeField,
      sizeField,
      xField,
      yField,
      defaultSize,
      posStep = 0.02,
      sizeStep = 1,
      isQR,
      noControls,
      hasTitleOverflow,
    } = fieldConfig;

    const isVisible = visibilityKey ? config.fieldVisibility?.[visibilityKey] !== false : true;
    const currentFontSize = fontSizeField ? (config.layout?.[fontSizeField] || defaultSize) : null;
    const currentSize = sizeField ? (config.layout?.[sizeField] || defaultSize) : null;
    
    // Get current X/Y positions with fallback to template defaults or computed defaults
    const templateLayout = templateDetails.layout || {};
    
    // Compute sensible defaults for fields that may not be in template layout
    const getDefaultX = (field) => {
      if (templateLayout[field] !== undefined) return templateLayout[field];
      // Compute based on field type
      if (field === 'printsAvailableX') return cardWidth - 1.2;
      if (field === 'audioIconX') return cardWidth - 0.35;
      return 0.1; // Default left margin
    };
    
    const getDefaultY = (field) => {
      if (templateLayout[field] !== undefined) return templateLayout[field];
      // Compute based on field type
      if (field === 'printsAvailableY') return cardHeight - 0.2;
      if (field === 'audioIconY') return 0.15;
      return 0.15; // Default top margin
    };
    
    const currentX = xField ? (config.layout?.[xField] ?? getDefaultX(xField)) : 0;
    const currentY = yField ? (config.layout?.[yField] ?? getDefaultY(yField)) : 0;

    return (
      <div
        key={visibilityKey || label}
        className={`field-chip ${isVisible ? 'active' : 'inactive'}`}
      >
        {/* Visibility Toggle */}
        {visibilityKey ? (
          <button
            className="visibility-btn"
            onClick={() => updateVisibility(visibilityKey, !isVisible)}
            title={isVisible ? `Hide ${label}` : `Show ${label}`}
          >
            {isVisible ? (
              <Eye size={20} strokeWidth={2} />
            ) : (
              <EyeOff size={20} strokeWidth={2} />
            )}
          </button>
        ) : (
          <div className="visibility-placeholder" />
        )}

        {/* Label */}
        <span className="field-label">{label}</span>

        {/* Controls only when visible and has controls */}
        {isVisible && !noControls && (
          <div className="field-controls">
            {/* Font Size / QR Size */}
            {(fontSizeField || sizeField) && (
              <div className="size-control">
                <button
                  className="ctrl-btn"
                  onClick={() => {
                    if (fontSizeField) {
                      updateLayout(fontSizeField, Math.max(6, currentFontSize - sizeStep));
                    } else if (sizeField) {
                      updateLayout(sizeField, Math.max(0.2, currentSize - sizeStep));
                    }
                  }}
                  title="Decrease size"
                >
                  −
                </button>
                <span className="size-value">
                  {isQR ? `${currentSize?.toFixed(1)}"` : currentFontSize}
                </span>
                <button
                  className="ctrl-btn"
                  onClick={() => {
                    if (fontSizeField) {
                      updateLayout(fontSizeField, Math.min(48, currentFontSize + sizeStep));
                    } else if (sizeField) {
                      updateLayout(sizeField, Math.min(2, currentSize + sizeStep));
                    }
                  }}
                  title="Increase size"
                >
                  +
                </button>
              </div>
            )}

            {/* Position Controls (X/Y) for all fields with position fields */}
            {xField && yField && (
              <div className="position-control">
                <button
                  className="pos-btn"
                  onClick={() => updateLayout(xField, currentX - posStep)}
                  onMouseDown={() => startHold(() => updateLayout(xField, (config.layout?.[xField] ?? currentX) - posStep * HOLD_STEP_MULTIPLIER))}
                  onMouseUp={stopHold}
                  onMouseLeave={stopHold}
                  onTouchStart={() => startHold(() => updateLayout(xField, (config.layout?.[xField] ?? currentX) - posStep * HOLD_STEP_MULTIPLIER))}
                  onTouchEnd={stopHold}
                  title="Click: fine tune | Hold: fast move"
                >
                  <ChevronLeft size={12} />
                </button>
                <div className="pos-vertical">
                  <button
                    className="pos-btn"
                    onClick={() => updateLayout(yField, currentY - posStep)}
                    onMouseDown={() => startHold(() => updateLayout(yField, (config.layout?.[yField] ?? currentY) - posStep * HOLD_STEP_MULTIPLIER))}
                    onMouseUp={stopHold}
                    onMouseLeave={stopHold}
                    onTouchStart={() => startHold(() => updateLayout(yField, (config.layout?.[yField] ?? currentY) - posStep * HOLD_STEP_MULTIPLIER))}
                    onTouchEnd={stopHold}
                    title="Click: fine tune | Hold: fast move"
                  >
                    <ChevronUp size={12} />
                  </button>
                  <button
                    className="pos-btn"
                    onClick={() => updateLayout(yField, currentY + posStep)}
                    onMouseDown={() => startHold(() => updateLayout(yField, (config.layout?.[yField] ?? currentY) + posStep * HOLD_STEP_MULTIPLIER))}
                    onMouseUp={stopHold}
                    onMouseLeave={stopHold}
                    onTouchStart={() => startHold(() => updateLayout(yField, (config.layout?.[yField] ?? currentY) + posStep * HOLD_STEP_MULTIPLIER))}
                    onTouchEnd={stopHold}
                    title="Click: fine tune | Hold: fast move"
                  >
                    <ChevronDown size={12} />
                  </button>
                </div>
                <button
                  className="pos-btn"
                  onClick={() => updateLayout(xField, currentX + posStep)}
                  onMouseDown={() => startHold(() => updateLayout(xField, (config.layout?.[xField] ?? currentX) + posStep * HOLD_STEP_MULTIPLIER))}
                  onMouseUp={stopHold}
                  onMouseLeave={stopHold}
                  onTouchStart={() => startHold(() => updateLayout(xField, (config.layout?.[xField] ?? currentX) + posStep * HOLD_STEP_MULTIPLIER))}
                  onTouchEnd={stopHold}
                  title="Click: fine tune | Hold: fast move"
                >
                  <ChevronRight size={12} />
                </button>
              </div>
            )}

            {/* Title overflow control - inline with Title field */}
            {hasTitleOverflow && (
              <select
                value={titleFitMode}
                onChange={(e) => onTitleFitModeChange(e.target.value)}
                className="title-overflow-select"
                title="Title overflow behavior"
              >
                <option value="wrap">Wrap</option>
                <option value="shrink">Shrink</option>
                <option value="single-line-auto">1 line</option>
                <option value="ellipsis">...</option>
              </select>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="template-controls-panel-v2">
      {/* Template Selector */}
      <div className="template-header">
        <select
          value={selectedTemplate}
          onChange={(e) => onTemplateChange(e.target.value)}
          className="template-select"
        >
          {Object.keys(AVERY_TEMPLATES).map((key) => (
            <option key={key} value={key}>
              {AVERY_TEMPLATES[key].name}
            </option>
          ))}
        </select>
        <span className="template-meta">
          {cardsPerPage} per page ({cardsPerRow}×{cardsPerCol})
        </span>
      </div>

      {/* Artwork Navigation - own row below dropdown */}
      {hasMultipleArtworks && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', padding: '4px 0' }}>
          <button
            onClick={goToPrevArtwork}
            disabled={currentArtworkIndex === 0}
            title="Previous artwork"
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              width: '24px', height: '24px', padding: 0,
              border: '1px solid #d1d5db', borderRadius: '4px',
              background: 'white', cursor: currentArtworkIndex === 0 ? 'not-allowed' : 'pointer',
              opacity: currentArtworkIndex === 0 ? 0.4 : 1
            }}
          >
            <ChevronLeft size={14} />
          </button>

          {showSearch ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px', position: 'relative' }}>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search..."
                autoFocus
                style={{
                  width: '120px', padding: '4px 8px',
                  border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '12px'
                }}
              />
              <button
                onClick={() => { setShowSearch(false); setSearchQuery(''); }}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  width: '18px', height: '18px', padding: 0,
                  border: 'none', background: 'transparent', cursor: 'pointer', color: '#6b7280'
                }}
              >
                <X size={12} />
              </button>
              {filteredArtworks.length > 0 && (
                <div style={{
                  position: 'absolute', top: '100%', left: 0, right: 0, marginTop: '4px',
                  background: 'white', border: '1px solid #d1d5db', borderRadius: '6px',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)', zIndex: 100, maxHeight: '180px', overflowY: 'auto'
                }}>
                  {filteredArtworks.map((art) => (
                    <button
                      key={art.vc_id}
                      onClick={() => selectArtwork(art.originalIndex)}
                      style={{
                        display: 'flex', flexDirection: 'column', width: '100%',
                        padding: '6px 10px', border: 'none', background: 'transparent',
                        cursor: 'pointer', textAlign: 'left', borderBottom: '1px solid #f3f4f6'
                      }}
                    >
                      <span style={{ fontSize: '12px', fontWeight: 500, color: '#1f2937' }}>{art.title || 'Untitled'}</span>
                      <span style={{ fontSize: '10px', color: '#6b7280' }}>#{art.vc_id} · {art.nickname || 'Unknown'}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <button
                onClick={() => setShowSearch(true)}
                title="Search artworks"
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  width: '20px', height: '20px', padding: 0,
                  border: 'none', background: 'transparent', cursor: 'pointer', color: '#6b7280'
                }}
              >
                <Search size={12} />
              </button>
              <span style={{ fontSize: '12px', color: '#6b7280', whiteSpace: 'nowrap' }}>
                {currentArtworkIndex + 1}/{artworks.length}
              </span>
            </div>
          )}

          <button
            onClick={goToNextArtwork}
            disabled={currentArtworkIndex === artworks.length - 1}
            title="Next artwork"
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              width: '24px', height: '24px', padding: 0,
              border: '1px solid #d1d5db', borderRadius: '4px',
              background: 'white', cursor: currentArtworkIndex === artworks.length - 1 ? 'not-allowed' : 'pointer',
              opacity: currentArtworkIndex === artworks.length - 1 ? 0.4 : 1
            }}
          >
            <ChevronRight size={14} />
          </button>
        </div>
      )}

      {/* Preview - rendered as image for reliable QR code display */}
      <div className="preview-box" key={selectedTemplate}>
        <div
          className="preview-frame"
          style={{
            width: previewWidth,
            minHeight: previewHeight,
            overflow: 'visible',
          }}
        >
          {previewImageUrl ? (
            <img
              src={previewImageUrl}
              alt="Label preview"
              style={{
                width: '100%',
                height: 'auto',
                display: 'block',
              }}
            />
          ) : (
            <div className="preview-msg">Loading preview...</div>
          )}
        </div>

        <div className="preview-tag">LIVE PREVIEW</div>
      </div>

      {/* Field Chips */}
      <div className="field-chips-container">
        {fieldConfigs.map(renderFieldChip)}
      </div>
    </div>
  );
};

export default TemplateControlsPanel;
